import React from "react";
import { Box, Tabs, Tab } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import organizationIdSelector from "../../../atoms/organizationIdSelector";
import EditSupplements from "./admin/EditSupplements";
import EditIngredients from "./admin/EditIngredients";
import EditMealPlans from "./recipes/EditMealPlans";
import EditRecipes from "./recipes/EditRecipes";
import useEvents from "../../../hooks/useEvents";
import PageHeader from "../../../components/PageHeader";

const RecipesPage = ({ initialTab }) => {
  const location = useLocation();
  const events = useEvents();
  const organizationId = useRecoilValue(organizationIdSelector);
  const navigate = useNavigate();

  // Determine initial tab based on URL or prop
  const getInitialTab = () => {
    if (initialTab) return initialTab;

    const path = location.pathname;
    if (path === "/home/supplements") return "supplements";
    if (path === "/home/ingredients") return "ingredients";
    if (path === "/home/meal-plans") return "meal-plans";
    return "recipes";
  };

  const [tab, setTab] = React.useState(getInitialTab);

  // Update tab when location changes (for direct URL navigation)
  React.useEffect(() => {
    const newTab = getInitialTab();
    if (newTab !== tab) {
      setTab(newTab);
    }
  }, [location.pathname]);

  const handleTabChange = (event, newValue) => {
    events.logTabSwitch({
      pageName: "Nutrition",
      tabName: newValue,
      lastTab: tab,
    });
    setTab(newValue);

    // Update URL when tab changes
    switch (newValue) {
      case "supplements":
        navigate("/home/supplements");
        break;
      case "ingredients":
        navigate("/home/ingredients");
        break;
      case "meal-plans":
        navigate("/home/meal-plans");
        break;
      case "recipes":
      default:
        navigate("/home/recipes");
        break;
    }
  };

  const handleRecipeClick = (recipe) => {
    events.logSubpageAccess({
      pageType: "Recipes",
      pageName: recipe.id,
    });
    navigate(`/home/recipes/recipe/${organizationId}/${recipe.id}`);
  };

  const handleMealPlanClick = (mealPlan) => {
    events.logSubpageAccess({
      pageType: "Meal Plans",
      pageName: mealPlan.id,
    });
    navigate(`/home/recipes/mealplan/${organizationId}/${mealPlan.id}`);
  };

  // Get page title based on current tab
  const getPageTitle = () => {
    switch (tab) {
      case "supplements":
        return "Supplements";
      case "ingredients":
        return "Ingredients";
      case "meal-plans":
        return "Meal Plans";
      default:
        return "Recipes";
    }
  };

  return (
    <>
      <PageHeader
        title={getPageTitle()}
        subtitle="Discover and customize recipes for your meal plans"
      />
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ mt: 1, mb: 1 }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            aria-label="nutrition tabs"
          >
            <Tab label="Recipes" value="recipes" />
            <Tab label="Meal Plans" value="meal-plans" />
            <Tab label="Supplements" value="supplements" />
            <Tab label="Ingredients" value="ingredients" />
          </Tabs>
        </Box>

        {tab === "recipes" && <EditRecipes onShowDetails={handleRecipeClick} />}

        {tab === "meal-plans" && (
          <EditMealPlans onShowDetails={handleMealPlanClick} />
        )}

        {tab === "supplements" && <EditSupplements />}

        {tab === "ingredients" && <EditIngredients />}
      </Box>
    </>
  );
};

export default RecipesPage;
